import { IErrorResponseDto } from "../dtos/error-response.dto";

type UnknownRecord = Record<string, unknown>;

const DEFAULT_ERROR_NAME = "Error";
const DEFAULT_ERROR_CODE = "";
const DEFAULT_ERROR_STACK = "";
const DEFAULT_ERROR_STATUS_CODE = 500;

function isUnknownRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function getPayload(value: unknown): unknown {
  return isUnknownRecord(value) && "data" in value ? value.data : value;
}

function getValidationMessage(errors: unknown): string | undefined {
  if (!isUnknownRecord(errors)) {
    return undefined;
  }

  const messages = Object.values(errors).flatMap((errorValue) =>
    Array.isArray(errorValue)
      ? errorValue.filter((message): message is string => typeof message === "string")
      : []
  );

  return messages.length > 0 ? messages.join("\n") : undefined;
}

function getStatusCode(source: unknown, payload: unknown): number {
  if (isUnknownRecord(payload) && typeof payload.status === "number") {
    return payload.status;
  }

  if (isUnknownRecord(source) && typeof source.status === "number") {
    return source.status;
  }

  return DEFAULT_ERROR_STATUS_CODE;
}

function getNestedErrorMessage(error: unknown): string | undefined {
  return isUnknownRecord(error) && typeof error.message === "string"
    ? error.message
    : undefined;
}

function getMessage(source: unknown, payload: unknown): string {
  if (typeof payload === "string" && payload.length > 0) {
    return payload;
  }

  if (isUnknownRecord(source) && typeof source.error === "string") {
    return source.error;
  }

  if (isUnknownRecord(payload)) {
    return (
      getValidationMessage(payload.errors) ??
      (typeof payload.detail === "string" ? payload.detail : undefined) ??
      (typeof payload.title === "string" ? payload.title : undefined) ??
      getNestedErrorMessage(payload.error) ??
      DEFAULT_ERROR_NAME
    );
  }

  return DEFAULT_ERROR_NAME;
}

export function normalizeApiError(source: unknown): IErrorResponseDto {
  const payload = getPayload(source);

  return {
    error: {
      statusCode: getStatusCode(source, payload),
      name: DEFAULT_ERROR_NAME,
      message: getMessage(source, payload),
      code: DEFAULT_ERROR_CODE,
      stack: DEFAULT_ERROR_STACK,
    },
  };
}
